/* eslint-disable */
/**
 * ApiKeys — create and revoke keys for the public Brand API (/v1).
 *
 * Keys are shown exactly once, right after creation; the API only stores a
 * hash. Test keys act on a private sandbox seeded with test codes, so brands
 * can build an integration without touching real data.
 */
import { useCallback, useEffect, useState } from "react";
import {
  Card, CardBody, Badge, Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label, Input, Spinner,
} from "reactstrap";
import { toast } from "react-toastify";
import api from "api";
import { Store } from "StoreContext";
import { mediaUrl } from "config";

const cardStyle = { borderRadius: 12, boxShadow: "0 1px 3px rgba(0,0,0,0.06)", border: "none", marginBottom: 16 };
const th = { fontSize: 12, color: "#6B7280", fontWeight: 600, padding: "8px 10px", borderBottom: "1px solid #eef0f3", textAlign: "left", whiteSpace: "nowrap" };
const td = { fontSize: 13, color: "#374151", padding: "10px", borderBottom: "1px solid #f3f4f6", verticalAlign: "middle" };

const TYPE_HELP = {
  secret: "Server-side only. Full access to your brand's data.",
  publishable: "Safe in browsers and apps. Can only verify codes.",
};

const fmt = (d) => (d ? new Date(d).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "Never");

const ApiKeys = () => {
  const { user } = Store() || {};
  const isOwner = user?.role === "brand" && (user?.brandRole || "owner") === "owner";
  const docsUrl = `${(mediaUrl || "https://api.getveriscan.com/").replace(/\/$/, "")}/docs`;

  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [type, setType] = useState("secret");
  const [mode, setMode] = useState("test");
  const [creating, setCreating] = useState(false);
  const [revealed, setRevealed] = useState(null); // { key, apiKey }
  const [confirmRevoke, setConfirmRevoke] = useState(null);

  const load = useCallback(() => {
    if (!isOwner) { setLoading(false); return; }
    setLoading(true);
    api("get", "/api-keys")
      .then((res) => { if (res?.success) setKeys(res.keys || []); })
      .finally(() => setLoading(false));
  }, [isOwner]);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    if (e) e.preventDefault();
    setCreating(true);
    try {
      const res = await api("post", "/api-keys", { name: name.trim(), type, mode });
      if (res?.success) {
        setShowCreate(false);
        setName("");
        setRevealed({ key: res.key, apiKey: res.apiKey });
        load();
      }
    } catch (_) {
      // api() already showed a toast
    } finally {
      setCreating(false);
    }
  };

  const revoke = async () => {
    const k = confirmRevoke;
    setConfirmRevoke(null);
    try {
      const res = await api("delete", `/api-keys/${k._id}`);
      if (res?.success) { toast.success("Key revoked. It stopped working immediately."); load(); }
    } catch (_) { /* toast shown */ }
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied"); }
    catch (_) { toast.error("Copy failed — select the key and copy it manually"); }
  };

  if (!isOwner) {
    return (
      <Card style={cardStyle}><CardBody style={{ fontSize: 14, color: "#374151" }}>
        Only the brand owner can manage API keys.
      </CardBody></Card>
    );
  }

  return (
    <div>
      <Card style={cardStyle}>
        <CardBody style={{ display: "flex", gap: 16, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
          <div style={{ fontSize: 13, color: "#374151", maxWidth: 560 }}>
            Start with a <strong>test</strong> key. It works against a private sandbox with 25 test codes, so
            nothing touches your real data.{" "}
            <a href={docsUrl} target="_blank" rel="noreferrer">Read the API docs</a>.
          </div>
          <button type="button" className="vs-btn-save" onClick={() => setShowCreate(true)} style={{ whiteSpace: "nowrap" }}>
            Create key
          </button>
        </CardBody>
      </Card>

      <Card style={cardStyle}>
        <CardBody style={{ padding: 0 }}>
          {loading ? (
            <div style={{ padding: 24, textAlign: "center" }}><Spinner size="sm" /></div>
          ) : keys.length === 0 ? (
            <div style={{ padding: 24, fontSize: 13, color: "#6B7280", textAlign: "center" }}>No API keys yet.</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr><th style={th}>Name</th><th style={th}>Key</th><th style={th}>Type</th><th style={th}>Mode</th><th style={th}>Last used</th><th style={th}>Created</th><th style={th}></th></tr>
                </thead>
                <tbody>
                  {keys.map((k) => (
                    <tr key={k._id}>
                      <td style={td}>{k.name || <span style={{ color: "#9CA3AF" }}>Untitled</span>}</td>
                      <td style={{ ...td, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 12, whiteSpace: "nowrap" }}>{k.prefix}…{k.last4}</td>
                      <td style={td}>{k.type === "secret" ? "Secret" : "Publishable"}</td>
                      <td style={td}>
                        <Badge style={{ background: k.mode === "live" ? "rgba(0,178,7,0.1)" : "rgba(245,158,11,0.12)", color: k.mode === "live" ? "#067a47" : "#b45309", fontWeight: 600 }}>
                          {k.mode === "live" ? "Live" : "Test"}
                        </Badge>
                      </td>
                      <td style={{ ...td, whiteSpace: "nowrap" }}>{fmt(k.lastUsedAt)}</td>
                      <td style={{ ...td, whiteSpace: "nowrap" }}>{fmt(k.createdAt)}</td>
                      <td style={{ ...td, textAlign: "right" }}>
                        <button type="button" className="vs-btn-outline-pill" onClick={() => setConfirmRevoke(k)} style={{ fontSize: 12, padding: "4px 12px", color: "#dc3545", borderColor: "#dc3545" }}>
                          Revoke
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Create */}
      <Modal isOpen={showCreate} toggle={() => setShowCreate(false)}>
        <ModalHeader toggle={() => setShowCreate(false)}>Create API key</ModalHeader>
        <form onSubmit={create}>
          <ModalBody>
            <FormGroup>
              <Label className="vs-label">Name (optional)</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Website, Shopify sync" maxLength={80} />
            </FormGroup>
            <FormGroup>
              <Label className="vs-label">Type</Label>
              <Input type="select" value={type} onChange={(e) => setType(e.target.value)}>
                <option value="secret">Secret</option>
                <option value="publishable">Publishable</option>
              </Input>
              <div style={{ fontSize: 12, color: "#6B7280", marginTop: 4 }}>{TYPE_HELP[type]}</div>
            </FormGroup>
            <FormGroup style={{ marginBottom: 0 }}>
              <Label className="vs-label">Mode</Label>
              <Input type="select" value={mode} onChange={(e) => setMode(e.target.value)}>
                <option value="test">Test — sandbox data</option>
                <option value="live">Live — real codes and customers</option>
              </Input>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <button type="button" className="vs-btn-cancel" onClick={() => setShowCreate(false)}>Cancel</button>
            <button type="submit" className="vs-btn-save" disabled={creating}>{creating ? "Creating…" : "Create key"}</button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Reveal once */}
      <Modal isOpen={!!revealed} toggle={() => setRevealed(null)} backdrop="static">
        <ModalHeader>Copy your new key</ModalHeader>
        <ModalBody>
          <p style={{ fontSize: 13, color: "#374151" }}>
            This is the only time the full key is shown. Store it somewhere safe.
            {revealed?.apiKey?.type === "secret" && " Keep secret keys on your server; never put them in website code."}
          </p>
          <div style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
            <code style={{ flex: 1, minWidth: 0, background: "#0f172a", color: "#e2e8f0", padding: "10px 12px", borderRadius: 8, fontSize: 12.5, overflowWrap: "anywhere" }}>
              {revealed?.key}
            </code>
            <button type="button" className="vs-btn-outline-pill" onClick={() => copy(revealed?.key)}>Copy</button>
          </div>
        </ModalBody>
        <ModalFooter>
          <button type="button" className="vs-btn-save" onClick={() => setRevealed(null)}>I've saved it</button>
        </ModalFooter>
      </Modal>

      {/* Revoke confirm */}
      <Modal isOpen={!!confirmRevoke} toggle={() => setConfirmRevoke(null)}>
        <ModalHeader toggle={() => setConfirmRevoke(null)}>Revoke key?</ModalHeader>
        <ModalBody style={{ fontSize: 13, color: "#374151" }}>
          <code>{confirmRevoke?.prefix}…{confirmRevoke?.last4}</code> will stop working immediately. Anything using it will start getting authentication errors.
        </ModalBody>
        <ModalFooter>
          <button type="button" className="vs-btn-cancel" onClick={() => setConfirmRevoke(null)}>Cancel</button>
          <button type="button" className="vs-btn-save" onClick={revoke} style={{ background: "#dc3545", borderColor: "#dc3545" }}>Revoke</button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ApiKeys;
